document.addEventListener('DOMContentLoaded', async () => {
  const form = document.querySelector('.homepage-hero__car-select form.homepage-hero__form');
  if (!form) return;

  const FIELD_ORDER = ['brand', 'model', 'version', 'generation', 'bodytype'];

  /* Create a mapping from field type to its index in the order
    "brand": 0,
    "model": 1,
    "version": 2,
    "generation": 3,
    "bodytype": 4
   */
  const FIELD_INDEX = FIELD_ORDER.reduce((acc, field, index) => {
    acc[field] = index;
    return acc;
  }, {});
    // Brand priority
    const PRIORITY_BRANDS = ['Audi', 'BMW', 'Mercedes-Benz', 'Porsche', 'Volkswagen'].map(brand => brand.toLowerCase());

    const getBrandRank = label => {
      const index = PRIORITY_BRANDS.indexOf(label.trim().toLowerCase());
      return index === -1 ? PRIORITY_BRANDS.length : index;
    };

  const selects = {
    brand: form.querySelector('#car-select-form-brand'),
    model: form.querySelector('#car-select-form-model'),
    version: form.querySelector('#car-select-form-version'),
    generation: form.querySelector('#car-select-form-generation'),
    bodytype: form.querySelector('#car-select-form-body-type')
  };

  if (!selects.brand || !selects.model || !selects.version || !selects.generation || !selects.bodytype) return;

  const getPlaceholderLabel = select => select.querySelector('option')?.textContent?.trim() || '';

  const placeholders = {
    brand: getPlaceholderLabel(selects.brand),
    model: getPlaceholderLabel(selects.model),
    version: getPlaceholderLabel(selects.version),
    generation: getPlaceholderLabel(selects.generation),
    bodytype: getPlaceholderLabel(selects.bodytype)
  };

  let globalData = window?.MaxtonVehicleData;

  if ((!globalData || !Array.isArray(globalData.nodes)) && window?.MaxtonVehicleDataPromise) {
    try {
      globalData = await window.MaxtonVehicleDataPromise;
    } catch (error) {
      console.error('[homepage-hero-car-select] Failed to load vehicle taxonomy.', error);
    }
  }

  if (!globalData || !Array.isArray(globalData.nodes)) {
    return;
  }

  // Extracts the first 4-digit year found in a generation label like
  // "G22 [2020-2024]" or "G26 [2021-]" — used to sort generations
  // chronologically instead of alphabetically. Labels with no year sort last.
  const extractGenerationStartYear = (label) => {
    const match = label.match(/\[(\d{4})/);
    return match ? Number(match[1]) : Infinity;
  };

  /* Vehicle dataset helper:
   Normalizes `window.MaxtonVehicleData` into lookup maps (nodesById, childrenByParent)
   Computes the valid options for each select based on current selections
   Mirrors the car drawer's bodytype "flattening" behavior
  */
  const dataManager = {
    prepareData(rawData) {
      const nodes = (rawData.nodes ?? [])
        .map((node) => ({
          id: Number(node.id),
          parentId: Number(node.parentId),
          type: node.type ?? '',
          label: node.label ?? '',
          link: node.link ?? ''
        }))
        .filter((node, index) => Number.isFinite(node.id) && Number.isFinite(node.parentId) && !rawData.nodes[index]?.hide);

      const nodesById = new Map();
      const childrenByParent = new Map();

      nodes.forEach((node) => {
        nodesById.set(node.id, node);

        if (!childrenByParent.has(node.parentId)) {
          childrenByParent.set(node.parentId, []);
        }
        childrenByParent.get(node.parentId).push(node);
      });

      childrenByParent.forEach((children) => {
        children.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
      });

      // 1876 is the default rootId for brands
      const brandsRootId = 1876;

      return {
        rootId: Number(rawData.rootId) || brandsRootId,
        nodes,
        nodesById,
        childrenByParent
      };
    },

    formatOptions(nodes) {
      const sorted = [...nodes].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

      return sorted.map(node => ({ id: node.id, label: node.label, node }));
    },
    formatBrandOptions(nodes) {
      const sorted = [...nodes].sort((a, b) =>
        getBrandRank(a.label) - getBrandRank(b.label)
        || a.label.localeCompare(b.label, undefined, { numeric: true }));

      return sorted.map(node => ({ id: node.id, label: node.label, node }));
    },

    // Same as formatOptions, but sorts by the generation's start year first
    // (from a label like "G22 [2020-2024]"), falling back to alphabetical
    // order for generations that share the same start year.
    formatGenerationOptions(nodes) {
      const sorted = [...nodes].sort((a, b) => {
        const yearDiff = extractGenerationStartYear(a.label) - extractGenerationStartYear(b.label);
        if (yearDiff !== 0) return yearDiff;
        return a.label.localeCompare(b.label, undefined, { numeric: true });
      });

      return sorted.map(node => ({ id: node.id, label: node.label, node }));
    },

    getOptionsForSelect(fieldType, selectionNodes, carData) {
      if (!carData) return [];

      if (fieldType === 'brand') {
        const children = carData.childrenByParent.get(carData.rootId) ?? [];
        return this.formatBrandOptions(children);
      }

      if (!(fieldType in FIELD_INDEX)) return [];

      if (fieldType === 'bodytype') {
        return this.getBodyTypeOptions(selectionNodes, carData);
      }

      const previousField = FIELD_ORDER[FIELD_INDEX[fieldType] - 1];
      const parentNode = selectionNodes?.[previousField];
      if (!parentNode) return [];

      const children = carData.childrenByParent.get(parentNode.id) ?? [];
      const typed = children.filter(child => child.type === fieldType);
      const nodesToFormat = typed.length > 0 ? typed : children;

      if (fieldType === 'generation') {
        return this.formatGenerationOptions(nodesToFormat);
      }

      return this.formatOptions(nodesToFormat);
    },

    getBodyTypeOptions(selectionNodes, carData) {
      const generationNode = selectionNodes?.generation;
      if (generationNode) {
        const children = carData.childrenByParent.get(generationNode.id) ?? [];
        return children.length > 0 ? this.expandBodyTypeOptions(children, carData) : [];
      }

      const versionNode = selectionNodes?.version;
      if (!versionNode) return [];

      const versionChildren = carData.childrenByParent.get(versionNode.id) ?? [];

      // If version's children are generation-type nodes, bodytype must wait for generation selection
      if (versionChildren.some(child => child.type === 'generation')) return [];

      return versionChildren.length > 0 ? this.expandBodyTypeOptions(versionChildren, carData) : [];
    },

    expandBodyTypeOptions(nodes, carData) {
      const options = [];

      nodes.forEach((node) => {
        const childNodes = carData.childrenByParent.get(node.id) ?? [];

        if (childNodes.length === 0) {
          options.push({
            id: node.id,
            label: node.label,
            node
          });
          return;
        }

        childNodes.forEach(child => {
          options.push({
            id: child.id,
            label: `${node.label} - ${child.label}`,
            node: child
          });
        });
      });

      options.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

      return options;
    }
  };

  const carData = dataManager.prepareData(globalData);
  const selectionNodes = {};

  const getNodeDepth = node => {
    if (!node) return -1;

    const visited = new Set();
    let depth = 0;
    let current = node;

    while (current && current.parentId !== carData.rootId) {
      if (visited.has(current.id)) return -1;
      visited.add(current.id);

      current = carData.nodesById.get(current.parentId);
      if (!current) return -1;
      depth += 1;
    }

    return current?.parentId === carData.rootId ? depth + 1 : -1;
  };

  const getNodeChainToRoot = (node) => {
    const chain = [];
    const visited = new Set();
    let current = node;

    while (current) {
      if (visited.has(current.id)) return null;
      visited.add(current.id);

      chain.unshift(current);

      if (current.parentId === carData.rootId) break;
      current = carData.nodesById.get(current.parentId);
    }

    if (chain.length === 0) return null;
    if (chain[0]?.parentId !== carData.rootId) return null;
    return chain;
  };

  const selectHasOptionValue = (select, value) => {
    if (!select) return false;
    return [...select.options].some(option => option.value === value);
  };

  const normalizeLabel = value => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

  const findChildByLabel = (parentId, label) => {
    const target = normalizeLabel(label);
    if (!target) return null;
    const children = carData.childrenByParent.get(Number(parentId)) ?? [];

    return children.find(child => normalizeLabel(child.label) === target) ?? null;
  };

  const findBestNodeByLink = link => {
    const target = String(link ?? '').trim();
    if (!target) return null;

    let bestNode = null;
    let bestDepth = -1;

    carData.nodes.forEach(node => {
      const nodeLink = node?.link?.trim();
      if (!nodeLink || nodeLink !== target) return;

      const depth = getNodeDepth(node);
      if (depth > bestDepth) {
        bestDepth = depth;
        bestNode = node;
      }
    });

    return bestNode;
  };

  const resetSelect = (select, placeholder) => {
    select.innerHTML = '';
    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.disabled = true;
    placeholderOption.selected = true;
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    select.disabled = true;
    select.required = false;
  };

  const populateSelect = (select, placeholder, options) => {
    select.innerHTML = '';

    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.disabled = true;
    placeholderOption.selected = true;
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    options.forEach((opt) => {
      const option = document.createElement('option');
      option.value = String(opt.id);
      option.textContent = opt.label;
      select.appendChild(option);
    });
  };

  const determineRequiredFields = () => {
    const baseFields = ['brand'];

    // Only require downstream fields that actually have options for the current path.
    // This keeps validation dataset-driven (same idea as the car drawer flow).
    const followUp = FIELD_ORDER
      .slice(1)
      .filter(fieldType => dataManager.getOptionsForSelect(fieldType, selectionNodes, carData).length > 0);

    const required = [...baseFields, ...followUp];

    const modelNode = selectionNodes?.model;
    if (modelNode) {
      const versionOptions = carData.childrenByParent.get(modelNode.id) ?? [];
      if (versionOptions.length > 0 && !required.includes('version')) {
        required.push('version');
      }
    }

    return required;
  };

  const clearDownstream = fromFieldType => {
    const fromIndex = FIELD_ORDER.indexOf(fromFieldType);
    if (fromIndex === -1) return;

    FIELD_ORDER.slice(fromIndex + 1).forEach(fieldType => {
      delete selectionNodes[fieldType];
      resetSelect(selects[fieldType], placeholders[fieldType]);
    });
  };

  const updateAvailabilityAfterSelection = fromFieldType => {
    const selectedIndex = FIELD_ORDER.indexOf(fromFieldType);
    if (selectedIndex === -1) return;
  
    for (let i = selectedIndex + 1; i < FIELD_ORDER.length; i++) {
      const fieldType = FIELD_ORDER[i];
      const options = dataManager.getOptionsForSelect(fieldType, selectionNodes, carData);
  
      resetSelect(selects[fieldType], placeholders[fieldType]);
  
      if (options.length > 0) {
        populateSelect(selects[fieldType], placeholders[fieldType], options);
  
        selects[fieldType].disabled = false;    
      }
    }
  };
  
  

  const setSelectionFromSelect = fieldType => {
    const select = selects[fieldType];
    if (!select) return;

    const id = Number(select.value);
    if (!Number.isFinite(id)) {
      delete selectionNodes[fieldType];
      return;
    }

    const node = carData.nodesById.get(id);
    if (!node) {
      delete selectionNodes[fieldType];
      return;
    }

    selectionNodes[fieldType] = node;
  };

  const init = () => {
    const brandOptions = dataManager.getOptionsForSelect('brand', selectionNodes, carData);
    populateSelect(selects.brand, placeholders.brand, brandOptions);
    selects.brand.disabled = false;

    resetSelect(selects.model, placeholders.model);
    resetSelect(selects.version, placeholders.version);
    resetSelect(selects.generation, placeholders.generation);
    resetSelect(selects.bodytype, placeholders.bodytype);
  };

  const resetState = () => {
    Object.keys(selectionNodes).forEach(key => delete selectionNodes[key]);
    init();
  };

  const prefillFromSelectedVehicle = async ({ preferredIndex = null } = {}) => {
    if (typeof window.VehicleStorage !== 'function') return;

    const storage = new window.VehicleStorage();
    const savedVehiclesRaw = await storage.loadVehicles();
    const savedVehicles = Array.isArray(savedVehiclesRaw) ? savedVehiclesRaw : [];

    let savedVehicle = null;

    if (preferredIndex != null) {
      const position = Number(preferredIndex);
      if (Number.isFinite(position) && position > 0 && position <= savedVehicles.length) {
        savedVehicle = savedVehicles[position - 1] ?? null;
      }

      if (!savedVehicle) {
        savedVehicle = savedVehicles.find(vehicle => Number(vehicle?.index) === Number(preferredIndex)) ?? null;
      }
    }

    if (!savedVehicle) {
      const selectedCarName = await storage.loadSelected();
      if (!selectedCarName) return;
      savedVehicle = savedVehicles.find(vehicle => vehicle?.name === selectedCarName) ?? null;
    }

    const savedLink = savedVehicle?.link?.trim();
    const applyValue = (fieldType, id) => {
      const select = selects[fieldType];
      if (!select || id == null) return false;
      const value = String(id);
      if (!selectHasOptionValue(select, value)) return false;
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    };

    // Prefill from a saved vehicle `link` by locating the matching dataset node,
    // then walking ancestors back to the root and applying the chain in order.
    const restoreFromLink = link => {
      const bestNode = findBestNodeByLink(link);
      if (!bestNode) return false;

      const chain = getNodeChainToRoot(bestNode);
      if (!chain) return false;

      const chainBrand = chain[0];
      if (!chainBrand) return false;
      if (!applyValue('brand', chainBrand.id)) return false;

      const chainModel = chain[1];
      if (chainModel && !applyValue('model', chainModel.id)) return false;

      const chainVersion = chain[2];
      if (chainVersion && !applyValue('version', chainVersion.id)) return false;

      const chainGeneration = chain[3];
      if (chainGeneration && !applyValue('generation', chainGeneration.id)) return false;

      applyValue('bodytype', bestNode.id);
      return true;
    };

    if (savedLink) {
      const restored = restoreFromLink(savedLink);
      if (restored) return;
      resetState();
    }

    const vehicleData = savedVehicle?.data;
    if (!vehicleData) return;

    const brandNode = findChildByLabel(carData.rootId, vehicleData.brand);
    if (!brandNode) return;
    if (!applyValue('brand', brandNode.id)) return;

    const modelNode = findChildByLabel(brandNode.id, vehicleData.model);
    modelNode && applyValue('model', modelNode.id);

    const versionNode = modelNode ? findChildByLabel(modelNode.id, vehicleData.version) : null;
    versionNode && applyValue('version', versionNode.id);

    const generationNode = versionNode ? findChildByLabel(versionNode.id, vehicleData.generation) : null;
    generationNode && applyValue('generation', generationNode.id);

    const bodyTypeOptions = dataManager.getOptionsForSelect('bodytype', selectionNodes, carData);
    const desiredBody = normalizeLabel(vehicleData.bodytype);
    const bodyNode = bodyTypeOptions.find((option) => {
      if (!desiredBody) return false;
      const optionLabel = normalizeLabel(option.label);
      const nodeLabel = normalizeLabel(option.node?.label);

      return optionLabel === desiredBody || nodeLabel === desiredBody || optionLabel.endsWith(` - ${desiredBody}`);
    })?.node;

    applyValue('bodytype', bodyNode?.id ?? generationNode?.id);
  };

  selects.brand.addEventListener('change', () => {
    setSelectionFromSelect('brand');
    clearDownstream('brand');
    updateAvailabilityAfterSelection('brand');
  });

  selects.model.addEventListener('change', () => {
    setSelectionFromSelect('model');
    clearDownstream('model');
    updateAvailabilityAfterSelection('model');
  });

  selects.version.addEventListener('change', () => {
    setSelectionFromSelect('version');
    clearDownstream('version');
    updateAvailabilityAfterSelection('version');
  });

  selects.generation.addEventListener('change', () => {
    setSelectionFromSelect('generation');
    clearDownstream('generation');
    updateAvailabilityAfterSelection('generation');
  });

  selects.bodytype.addEventListener('change', () => {
    setSelectionFromSelect('bodytype');
  });
  // const getLocaleFromUrl = () => {
  //   const match = window.location.pathname.match(/^\/(pl|de|fr|en)(\/|$)/);
  //   return match ? match[1] : '';
  // };

  form.addEventListener('submit', async event => {
    event.preventDefault();

    if (!form.reportValidity()) return;

    const brand = selectionNodes?.brand?.label ?? '';
    const model = selectionNodes?.model?.label ?? '';
    const version = selectionNodes?.version?.label ?? '';
    const generation = selectionNodes?.generation?.label ?? '';
    const bodytype = selectionNodes?.bodytype?.label ?? '';

    const carDataToSave = { brand, model, version, generation, bodytype };

    const carNameParts = [brand, model].filter(Boolean);
    if (version) carNameParts.push(version);
    if (generation) carNameParts.push(generation);
    const carName = carNameParts.join(' ');

    const finalNode = selectionNodes.bodytype
      || selectionNodes.generation
      || selectionNodes.version
      || selectionNodes.model
      || selectionNodes.brand;

    const link = finalNode?.link?.trim();

    if (!link) {
      console.error('[homepage-hero-car-select] No link found for selected vehicle.', {
        selectionNodes,
        finalNode
      });
      return;
    }

    if (typeof window.VehicleStorage !== 'function') {
      console.error('[homepage-hero-car-select] VehicleStorage is not available.');
      return;
    }

    const storage = new window.VehicleStorage();
    const savedVehiclesRaw = await storage.loadVehicles();
    const savedVehicles = Array.isArray(savedVehiclesRaw) ? savedVehiclesRaw : [];

    const existingVehicle = savedVehicles.find(v => v?.link === link);
    const selectedCarName = existingVehicle?.name || carName;

    if (!existingVehicle) {
      const maxIndex = savedVehicles.reduce((max, vehicle) => {
        const value = Number(vehicle?.index);
        return Number.isFinite(value) ? Math.max(max, value) : max;
      }, 0);

      savedVehicles.push({
        name: carName,
        data: carDataToSave,
        link,
        index: maxIndex + 1,
        addedAt: new Date().toISOString()
      });

      await storage.saveVehicles(savedVehicles);

      document.dispatchEvent(new CustomEvent('car-added', {
        detail: { carData: carDataToSave, carName }
      }));
    }

    await storage.saveSelected(selectedCarName);

    document.dispatchEvent(new CustomEvent('car-selected', {
      detail: { carName: selectedCarName, element: form }
    }));

    //const locale = getLocaleFromUrl();
    window.location.href = link;
  });

  const syncFromStorage = ({ preferredIndex = null } = {}) => {
    resetState();
    prefillFromSelectedVehicle({ preferredIndex }).catch(error => {
      console.warn('[homepage-hero-car-select] Failed to prefill selection.', error);
    });
  };

  const getPreferredIndexFromEvent = event => {
    const element = event?.detail?.element;
    const value = element?.value;
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  document.addEventListener('car-selected', event => {
    const preferredIndex = getPreferredIndexFromEvent(event);
    syncFromStorage({ preferredIndex });
  });

  document.addEventListener('vehicles-deleted', () => {
    syncFromStorage();
  });

  document.addEventListener('car-added', () => {
    syncFromStorage();
  });

  syncFromStorage();
});